import { useState, useEffect } from 'react';

const PAGES_AUTH_TOKEN_KEY = 'pages-auth-token';
const TEN_MINUTES = 10 * 60 * 1000;
const APP_VERSION = 'home';

function getToken() {
  const authTokenString = localStorage.getItem(PAGES_AUTH_TOKEN_KEY);
  if (authTokenString) {
    return JSON.parse(authTokenString);
  }
  return null;
}

function setToken(token: any) {
  localStorage.setItem(PAGES_AUTH_TOKEN_KEY, JSON.stringify(token));
}

function railsCookieExists() {
  return document.cookie.indexOf('_rli=') >= 0;
}

function parseAuthToken(token: any) {
  /*
    An id token looks like header.payload.signature where the values between the dots
    are base64 encoded JSON. We want to extract the payload and pull out the relevant
    values.
  */

  const payload = token.split('.')[1];
  //decoded= { sub: string, roles: Array<string> }
  const decoded = JSON.parse(atob(payload));

  return {
    accountID: decoded.sub,
    isAdmin: !!(decoded.roles && decoded.roles.indexOf('admin') !== -1),
  };
}

function getRailsLoggedInAccountID() {
  const cookies = document.cookie.split('; ');
  for (const cookie of cookies) {
    const parts = cookie.split('=');
    if (parts[0] === '_rli') {
      return parts[1];
    }
  }

  return '';
}

export function removeToken() {
  localStorage.removeItem(PAGES_AUTH_TOKEN_KEY);
}

function createAuthToken(payload: any) {
  return {
    idToken: payload.id_token,
    refreshToken: payload.refresh_token,
    expiresAt: Date.now() + payload.expires_in * 1000,
  };
}

const serializeResponse = (response: any) => response.json();
const getData = (response: any) => response.data;

async function railsSingleSignOn() {
  const data = {
    use_cookies: true,
  };
  return fetch(`/flyer/api/auth/sso`, {
    method: 'POST',
    body: JSON.stringify(data),
    credentials: 'include',
    headers: { 'X-Client-Version': APP_VERSION },
  })
    .then(serializeResponse)
    .then(getData);
}

// We try to refresh the auth token when or before they expire
// eslint-disable-next-line
async function refreshAuthToken(refreshToken: any) {
  const data = {
    refresh_token: refreshToken,
  };

  return fetch(`/flyer/api/auth/refresh`, {
    method: 'POST',
    body: JSON.stringify(data),
    credentials: 'include',
    headers: { 'X-Client-Version': APP_VERSION },
  })
    .then(serializeResponse)
    .then(getData);
}

async function getFlyerAuthToken() {
  // simplified version of https://github.com/paperlesspost/pages/blob/1201c6161bc2f3b72632cbfd0d5e2f54b14a03b4/packages/pages_redux/src/sagas/user_state.saga.js#L79
  try {
    const existingAuthToken = getToken();

    if (existingAuthToken) {
      // Refresh if it's going to expire within 10 minutes
      const expiresWithinTenMinutes = existingAuthToken.expiresAt - TEN_MINUTES;
      if (Date.now() > expiresWithinTenMinutes) {
        // Use SSO if available, this ensures the Flyer session does not diverge from Core's
        const tokenResponse = await railsSingleSignOn();
        const newAuthToken = createAuthToken(tokenResponse);

        setToken(newAuthToken);
        return newAuthToken.idToken;
      } else {
        return existingAuthToken.idToken;
      }
    }

    if (railsCookieExists()) {
      let loggedInAccountID = '';
      if (existingAuthToken) {
        const tokenProps = parseAuthToken(existingAuthToken.idToken);
        loggedInAccountID = tokenProps.accountID;
      }
      // User logged in to Rails does not match user logged in to Flyer (mismatch or non-existing)
      const railsLoggedInAccountID = getRailsLoggedInAccountID();
      if (railsLoggedInAccountID !== loggedInAccountID) {
        // There is a mismatch but they are logged into Rails so try to match things up
        // Remove token from other account (if any)
        removeToken();

        const tokenResponse = await railsSingleSignOn();
        const newAuthToken = createAuthToken(tokenResponse);

        setToken(newAuthToken);
        return newAuthToken.idToken;
      }
    } else {
      removeToken();
      return null;
    }

    return '';
  } catch (err) {
    console.log(`failed getting id token: ${err}`);
    // Clear token on error if we support SSO, as SSO is able to get new
    // tokens based on Core's state
    removeToken();
  }
}

export default function useFlyerAuthToken() {
  const [flyerAuthToken, setFlyerAuthToken] = useState(null);

  useEffect(() => {
    const getToken = async () => {
      const token = await getFlyerAuthToken();
      setFlyerAuthToken(token);
    };
    getToken();
  }, []);

  return flyerAuthToken;
}
